import {ChangeDetectionStrategy, Component, inject, OnInit, signal} from '@angular/core';
import {AsyncPipe, DatePipe} from "@angular/common";
import {ConfirmationService, MessageService, PrimeTemplate} from "primeng/api";
import {TableModule} from "primeng/table";
import {ApplicationService} from "../../services/application.service";
import {TypedTableRowDirective} from "../../typed-table-row.directive";
import {RouterLink} from "@angular/router";
import {Button} from "primeng/button";
import {ApplicationDto} from "admin-api-dto";
import {finalize, forkJoin} from "rxjs";

@Component({
  selector: 'app-applications-tab',
  imports: [
    DatePipe,
    PrimeTemplate,
    TableModule,
    AsyncPipe,
    TypedTableRowDirective,
    RouterLink,
    Button
  ],
  templateUrl: './applications-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApplicationsTabComponent implements OnInit {
  private readonly applicationService = inject(ApplicationService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  protected readonly applications$ = this.applicationService.failedApplications$;
  protected readonly isRetryingFailedApplications = signal(false);
  protected readonly isDeletingApplications = signal(false);
  protected selectedApplications: ApplicationDto[] = [];

  ngOnInit() {
    this.applicationService.retrieveApplications().subscribe();
  }

  protected retryFailedApplications() {
    if (this.isRetryingFailedApplications()) {
      return;
    }

    this.isRetryingFailedApplications.set(true);

    this.applicationService.retryAllFailedApplications().pipe(
      finalize(() => this.isRetryingFailedApplications.set(false))
    ).subscribe({
      next: response => {
        this.messageService.add({
          severity: 'success',
          summary: 'Retry started',
          detail: `Queued retry for ${response.retried} failed ${response.retried === 1 ? 'application' : 'applications'}.`
        });

        this.applicationService.retrieveApplications().subscribe();
      },
      error: error => {
        console.error('Failed to retry failed applications', error);
        const detail = error instanceof Error ? error.message : 'Failed to retry failed lotteries.';
        this.messageService.add({
          severity: 'error',
          summary: 'Retry failed',
          detail
        });
      }
    });
  }

  protected deleteSelectedApplications() {
    if (this.isDeletingApplications() || this.selectedApplications.length === 0) {
      return;
    }

    const toDelete = [...this.selectedApplications];
    const count = toDelete.length;

    this.confirmationService.confirm({
      message: `Are you sure you want to delete ${count} failed ${count === 1 ? 'application' : 'applications'}? This cannot be undone.`,
      header: 'Delete failed applications',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.isDeletingApplications.set(true);

        forkJoin(toDelete.map(application => this.applicationService.deleteApplication(application.id))).pipe(
          finalize(() => this.isDeletingApplications.set(false))
        ).subscribe({
          next: () => {
            this.selectedApplications = [];
            this.messageService.add({
              severity: 'success',
              summary: 'Applications deleted',
              detail: `Deleted ${count} failed ${count === 1 ? 'application' : 'applications'}.`
            });
            this.applicationService.retrieveApplications().subscribe();
          },
          error: error => {
            console.error('Failed to delete selected applications', error);
            const detail = error instanceof Error ? error.message : 'Failed to delete selected applications.';
            this.messageService.add({
              severity: 'error',
              summary: 'Delete failed',
              detail
            });
            this.applicationService.retrieveApplications().subscribe();
          }
        });
      }
    });
  }
}
